import type { AccountsRouteKey } from '@charitystack/org-engagement-contracts';

export const accountsCutoverEligibleRoutes = Object.freeze([
  // Organization settings and custom fields.
  'GET /get-location',
  'PATCH /nonprofit-settings',
  'PATCH /global-custom-inputs',
  'PUT /org-slug',
  'GET /org-custom-fields',
  'POST /org-custom-fields',
  'PATCH /org-custom-fields',
  'DELETE /org-custom-fields',
  // Contact tags.
  'GET /org-contact-tags',
  'PUT /org-contact-tags',
  'DELETE /org-contact-tags',
  // User profile.
  'GET /users/me',
  'PATCH /users/me',
  'POST /users/me/active-org/sync',
  'POST /users',
  'GET /users',
  'PATCH /user-profile',
  // Roles and permissions.
  'GET /org-role-permissions',
  'PUT /org-role-permissions',
  'DELETE /org-role-permissions',
  'POST /custom-roles',
  'PATCH /custom-roles',
  'DELETE /custom-roles',
  'GET /clerk-users',
  'POST /clerk-users',
  'PATCH /clerk-users',
  'DELETE /clerk-users',
  // Billing plan, followed separately by merchant last.
  'PATCH /billing-plan',
  'GET /merchant',
] as const satisfies readonly AccountsRouteKey[]);

type AccountsCutoverRoute = (typeof accountsCutoverEligibleRoutes)[number];

type AccountsCutoverRow = {
  organizationId: string;
  enabled: boolean;
  enabledRoutes: readonly AccountsCutoverRoute[];
};

const CLERK_ORGANIZATION_ID = /^org_[A-Za-z0-9]+$/;

/**
 * P3 pilot (DK-36): one frozen row routing only that org's settings save, when `value` is a
 * well-formed Clerk organization id; otherwise the frozen empty list. The value comes from a
 * build setting a person fills in outside the repo; unset everywhere by default.
 */
export function p3PilotSettingsSaveRows(value: unknown): readonly AccountsCutoverRow[] {
  if (typeof value !== 'string' || !CLERK_ORGANIZATION_ID.test(value)) return Object.freeze([]);
  return Object.freeze([
    Object.freeze({
      organizationId: value,
      enabled: true,
      enabledRoutes: Object.freeze(['PATCH /nonprofit-settings'] as const),
    }),
  ]);
}

export const accountsApiCutover = Object.freeze({
  key: 'org_engagement_admin_api_cutover_accounts',
  // These are Clerk organization claim IDs (`o.id` / `org_id`), not persisted
  // CharityStack organization IDs. Population and activation require separate approval.
  // DK-36 approves populating only the P3 pilot org's settings-save row, from a build value.
  organizations: p3PilotSettingsSaveRows(import.meta.env.VITE_P3_PILOT_SETTINGS_SAVE_CLERK_ORG_ID),
});

export type AccountsRoutingDecision = Readonly<{
  organizationId: string | null;
  candidateEndpoint: string | null;
}>;

function decodeOrganizationId(authorization: string): string | null {
  const token = authorization.startsWith('Bearer ')
    ? authorization.slice('Bearer '.length).trim()
    : authorization;
  const parts = token.split('.');
  if (parts.length !== 3) return null;

  const base64 = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
  const claims: unknown = JSON.parse(atob(padded));
  if (!claims || typeof claims !== 'object') return null;

  const hasCompactOrg = Object.prototype.hasOwnProperty.call(claims, 'o');
  const compactOrg = (claims as { o?: unknown }).o;
  if (
    hasCompactOrg &&
    (!compactOrg || typeof compactOrg !== 'object' || Array.isArray(compactOrg))
  ) {
    return null;
  }
  const compactId = hasCompactOrg ? (compactOrg as { id?: unknown }).id : undefined;
  if (hasCompactOrg && (typeof compactId !== 'string' || !compactId)) return null;

  const hasLegacyId = Object.prototype.hasOwnProperty.call(claims, 'org_id');
  const legacyId = (claims as { org_id?: unknown }).org_id;
  if (hasLegacyId && (typeof legacyId !== 'string' || !legacyId)) return null;
  if (compactId && legacyId && compactId !== legacyId) return null;
  if (typeof compactId === 'string') return compactId;
  return typeof legacyId === 'string' ? legacyId : null;
}

/**
 * Selects transport from the organization claim in the current Clerk JWT. Decoding here is routing
 * context only, never signature verification or authorization; both remain backend responsibilities.
 */
export function resolveAccountsRouting({
  authorization,
  route,
  registry = accountsApiCutover.organizations,
  candidateEndpoint = import.meta.env.VITE_APP_ORG_ENGAGEMENT_TRPC_URL,
}: {
  authorization: string;
  route: unknown;
  registry?: unknown;
  candidateEndpoint?: unknown;
}): AccountsRoutingDecision {
  try {
    const organizationId = decodeOrganizationId(authorization);
    if (!organizationId || !Array.isArray(registry)) {
      return { organizationId, candidateEndpoint: null };
    }

    const eligibleRoutes = new Set<string>(accountsCutoverEligibleRoutes);
    const seenOrganizations = new Set<string>();
    let enabledForRoute = false;
    for (const row of registry) {
      const keys = row && typeof row === 'object' ? Object.keys(row).sort().join(',') : '';
      if (
        !row ||
        typeof row !== 'object' ||
        (keys !== 'enabled,organizationId' && keys !== 'enabled,enabledRoutes,organizationId') ||
        typeof row.organizationId !== 'string' ||
        !row.organizationId ||
        typeof row.enabled !== 'boolean' ||
        seenOrganizations.has(row.organizationId)
      ) {
        return { organizationId, candidateEndpoint: null };
      }
      seenOrganizations.add(row.organizationId);

      const enabledRoutes = row.enabledRoutes;
      if (enabledRoutes !== undefined) {
        if (!Array.isArray(enabledRoutes)) {
          return { organizationId, candidateEndpoint: null };
        }
        const seenRoutes = new Set<string>();
        for (const enabledRoute of enabledRoutes) {
          if (
            typeof enabledRoute !== 'string' ||
            !eligibleRoutes.has(enabledRoute) ||
            seenRoutes.has(enabledRoute)
          ) {
            return { organizationId, candidateEndpoint: null };
          }
          seenRoutes.add(enabledRoute);
        }
      }
      if (row.organizationId === organizationId) {
        enabledForRoute =
          row.enabled === true && Array.isArray(enabledRoutes) && enabledRoutes.includes(route);
      }
    }
    if (!enabledForRoute || typeof candidateEndpoint !== 'string' || !candidateEndpoint) {
      return { organizationId, candidateEndpoint: null };
    }

    const parsed = new URL(candidateEndpoint);
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      parsed.search ||
      parsed.hash
    ) {
      return { organizationId, candidateEndpoint: null };
    }
    return { organizationId, candidateEndpoint };
  } catch {
    return { organizationId: null, candidateEndpoint: null };
  }
}
